import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  buildCoParentInviteIdReturnUrl,
  consumePostAuthReturnUrl,
  extractCoParentInviteToken,
  peekCoParentInviteToken,
} from '../../auth/helpers/post-auth-return.helper';
import { FamilyHubService } from '../../parent-family/services/family-hub.service';
import { ParentIdentityService } from './parent-identity.service';

@Injectable({ providedIn: 'root' })
export class IdentityNavigationService {
  #router = inject(Router);
  #identity = inject(ParentIdentityService);
  #familyHub = inject(FamilyHubService);

  async navigateAfterAuth(layout?: string): Promise<void> {
    const resolvedLayout = layout || 'parent';

    if (resolvedLayout === 'school') {
      await this.#router.navigate(['/lobby']);
      return;
    }

    if (resolvedLayout !== 'parent') {
      const routes: Record<string, string> = {
        admin: '/office',
        patrol: '/setup',
        oauth: '/auth',
      };
      await this.#router.navigate([routes[resolvedLayout] ?? '/home']);
      return;
    }

    const destination = await this.resolveParentInviteDestination();
    if (destination) {
      await this.#router.navigateByUrl(destination);
      return;
    }

    await this.#identity.whenConfigReady();
    if (!this.#identity.isLayerEnabled()) {
      await this.#router.navigate(['/home']);
      return;
    }

    await this.#identity.reload();
    if (this.#identity.shouldPromptVerification()) {
      await this.#router.navigate(['/identity/verify']);
      return;
    }

    await this.#router.navigate(['/home']);
  }

  async navigateToVerification(): Promise<void> {
    await this.#router.navigate(['/identity/verify']);
  }

  /**
   * Invite return URL / stored token first; else first pending claimed incoming invite.
   * Claims invite by token when possible so hub incoming recovery works later.
   */
  async resolveParentInviteDestination(
    queryReturnUrl?: string | null,
  ): Promise<string | null> {
    const queryReturn =
      queryReturnUrl ??
      this.#router.parseUrl(this.#router.url).queryParams['returnUrl'];
    const query =
      typeof queryReturn === 'string' ? queryReturn : null;

    const tokenForClaim =
      extractCoParentInviteToken(query) ?? peekCoParentInviteToken();
    if (tokenForClaim) {
      await this.#familyHub.claimInviteByToken(tokenForClaim);
    }

    const familyReturn = consumePostAuthReturnUrl(query);
    if (familyReturn) {
      return familyReturn;
    }

    const incoming = await this.#familyHub.findPendingIncomingInvite();
    if (incoming?.id) {
      return buildCoParentInviteIdReturnUrl(incoming.id);
    }

    return null;
  }
}
